import { ManifestPartialReceiptLineStatus } from '../../../api/manifestPartialReceipt';
import { ManifestPackageStatus } from '../../../common/status';
import { ManifestPackageProps, ManifestProps } from '../../../datastore/tripSheets';

function getPrimaryLine(pkg: ManifestPackageProps) {
  return pkg.inventoryLines?.[0] || null;
}

function getPartialRowKey(pkg: ManifestPackageProps): string {
  const line = getPrimaryLine(pkg);
  const normalizedPackageId = String(pkg.id || '')
    .trim()
    .toLowerCase();
  const normalizedPoLineId = String(line?.orderLineId || '')
    .trim()
    .toLowerCase();
  const normalizedPartId = String(line?.partId || '')
    .trim()
    .toLowerCase();

  const rowIdentity =
    normalizedPoLineId || normalizedPartId
      ? `${normalizedPoLineId}::${normalizedPartId}`
      : `pkg::${normalizedPackageId}`;

  return `${String(pkg.asn || '')
    .trim()
    .toLowerCase()}::${rowIdentity}`;
}

function isActionLine(line: ManifestPartialReceiptLineStatus): boolean {
  const normalizedStatus = String(line.status || '')
    .trim()
    .toUpperCase();
  return line.selectedForReceive || normalizedStatus === 'FAILED' || Boolean(line.errorCode || line.errorMessage);
}

function setLatestLine(
  linesByKey: Map<string, ManifestPartialReceiptLineStatus>,
  key: string,
  line: ManifestPartialReceiptLineStatus,
  processedAt: number
) {
  const existingLine = linesByKey.get(key);
  const existingProcessedAt = existingLine?.processedAt ? new Date(existingLine.processedAt).getTime() : 0;

  if (!existingLine || processedAt >= existingProcessedAt) {
    linesByKey.set(key, line);
  }
}

function isPositiveSaveSnapshotLine(line: ManifestPartialReceiptLineStatus | undefined): boolean {
  if (!line || isActionLine(line)) {
    return false;
  }

  return Number(line.packageQuantity ?? 0) > 0;
}

function getSnapshotGroupKey(line: ManifestPartialReceiptLineStatus): string {
  return [
    line.sessionId,
    line.processedAt,
    String(line.packageId || '')
      .trim()
      .toLowerCase()
  ]
    .map((value) => String(value || ''))
    .join('::');
}

function buildLatestSnapshotQuantityByPackageId(lines: ManifestPartialReceiptLineStatus[]) {
  const latestGroupByPackageId = new Map<
    string,
    { processedAt: number; quantity: number; line: ManifestPartialReceiptLineStatus }
  >();
  const groupedLines = new Map<string, ManifestPartialReceiptLineStatus[]>();

  lines.forEach((line) => {
    const packageId = String(line.packageId || '').trim();
    if (!packageId || isActionLine(line) || line.packageQuantity === null || line.packageQuantity === undefined) {
      return;
    }

    const key = getSnapshotGroupKey(line);
    groupedLines.set(key, [...(groupedLines.get(key) || []), line]);
  });

  groupedLines.forEach((groupLines) => {
    const firstLine = groupLines[0];
    if (!firstLine?.packageId) return;

    const processedAt = firstLine.processedAt ? new Date(firstLine.processedAt).getTime() : 0;
    const quantity = Math.max(...groupLines.map((line) => Number(line.packageQuantity || 0)));
    const packageId = String(firstLine.packageId).trim();
    const existing = latestGroupByPackageId.get(packageId);

    if (!existing || processedAt >= existing.processedAt) {
      latestGroupByPackageId.set(packageId, {
        processedAt,
        quantity,
        line: {
          ...firstLine,
          packageQuantity: quantity
        }
      });
    }
  });

  return new Map(Array.from(latestGroupByPackageId.entries()).map(([packageId, value]) => [packageId, value.line]));
}

function chooseLatestOverlayLine(
  actionLine: ManifestPartialReceiptLineStatus | undefined,
  snapshotLine: ManifestPartialReceiptLineStatus | undefined
): ManifestPartialReceiptLineStatus | undefined {
  if (!actionLine) {
    return snapshotLine;
  }

  if (!snapshotLine) {
    return actionLine;
  }

  const actionProcessedAt = actionLine.processedAt ? new Date(actionLine.processedAt).getTime() : 0;
  const snapshotProcessedAt = snapshotLine.processedAt ? new Date(snapshotLine.processedAt).getTime() : 0;
  if (isPositiveSaveSnapshotLine(snapshotLine) && snapshotProcessedAt >= actionProcessedAt) {
    return snapshotLine;
  }

  return actionLine;
}

export function applyPartialReceiptQuantitiesToManifest(
  sourceManifest: ManifestProps,
  lines: ManifestPartialReceiptLineStatus[]
): ManifestProps {
  const latestLineByPackageId = new Map<string, ManifestPartialReceiptLineStatus>();
  const latestLineByKey = new Map<string, ManifestPartialReceiptLineStatus>();
  const latestActionLineByPackageId = new Map<string, ManifestPartialReceiptLineStatus>();
  const latestActionLineByKey = new Map<string, ManifestPartialReceiptLineStatus>();
  const latestSnapshotQuantityByPackageId = buildLatestSnapshotQuantityByPackageId(lines);

  for (const line of lines) {
    const packageId = String(line.packageId || '').trim();
    const currentProcessedAt = line.processedAt ? new Date(line.processedAt).getTime() : 0;

    if (packageId) {
      setLatestLine(latestLineByPackageId, packageId, line, currentProcessedAt);
      if (isActionLine(line)) {
        setLatestLine(latestActionLineByPackageId, packageId, line, currentProcessedAt);
      }
    }

    const key = `${String(line.asn || '')
      .trim()
      .toLowerCase()}::${(() => {
      const normalizedPoLineId = String(line.poLineId || '')
        .trim()
        .toLowerCase();
      const normalizedItemId = String(line.itemId || '')
        .trim()
        .toLowerCase();
      const normalizedPackageId = String(line.packageId || '')
        .trim()
        .toLowerCase();

      if (normalizedPoLineId || normalizedItemId) {
        return `${normalizedPoLineId}::${normalizedItemId}`;
      }

      return `pkg::${normalizedPackageId}`;
    })()}`;

    if (key !== '::') {
      setLatestLine(latestLineByKey, key, line, currentProcessedAt);
      if (isActionLine(line)) {
        setLatestLine(latestActionLineByKey, key, line, currentProcessedAt);
      }
    }
  }

  return {
    ...sourceManifest,
    ssccs: sourceManifest.ssccs.map((sscc) => ({
      ...sscc,
      packages: sscc.packages.map((pkg) => {
        const fallbackKey = getPartialRowKey(pkg);
        const latestActionLine = latestActionLineByPackageId.get(pkg.id) || latestActionLineByKey.get(fallbackKey);
        const latestSnapshotLine =
          latestSnapshotQuantityByPackageId.get(pkg.id) ||
          latestLineByPackageId.get(pkg.id) ||
          latestLineByKey.get(fallbackKey);
        const latestLine = chooseLatestOverlayLine(latestActionLine, latestSnapshotLine);

        if (!latestLine) {
          const isQmStatus =
            pkg.receivedStatus === ManifestPackageStatus.qm || pkg.receivedStatus === ManifestPackageStatus.qm_pending;

          return {
            ...pkg,
            quantity: isQmStatus ? 0 : pkg.quantity,
            receivedStatus: pkg.receivedStatus
          };
        }

        const latestPositiveSnapshotLine = isPositiveSaveSnapshotLine(latestSnapshotLine)
          ? latestSnapshotLine
          : undefined;
        const rawQuantity =
          latestLine.packageQuantity ?? latestPositiveSnapshotLine?.packageQuantity ?? pkg.quantity ?? 0;
        const maxQuantity = Number(pkg.maxQuantity || 0);
        const hasMaxQuantity = Number.isFinite(maxQuantity) && maxQuantity > 0;
        const normalizedQuantity = Number(rawQuantity);
        const boundedQuantity = Number.isNaN(normalizedQuantity)
          ? 0
          : Math.max(0, hasMaxQuantity ? Math.min(normalizedQuantity, maxQuantity) : normalizedQuantity);

        const normalizedStatus = String(latestLine.status || '')
          .trim()
          .toUpperCase();

        const isDirectReceiptStatus =
          normalizedStatus === 'DIRECT_RECEIPTED' ||
          normalizedStatus === 'DIRECT RECEIPTED' ||
          normalizedStatus === 'DIRECT_PENDING' ||
          normalizedStatus === 'DIRECT PENDING';
        const hasPackageQuantitySource = latestLine.packageQuantity != null || Boolean(latestPositiveSnapshotLine);
        const receiveQuantityStatus =
          boundedQuantity === 0
            ? ManifestPackageStatus.notReceived
            : hasMaxQuantity && boundedQuantity < maxQuantity
              ? ManifestPackageStatus.partial
              : ManifestPackageStatus.received;

        const updatedStatus =
          normalizedStatus === 'QM_RAISED' || normalizedStatus === 'QM RAISED'
            ? ManifestPackageStatus.qm
            : isDirectReceiptStatus
              ? ManifestPackageStatus.direct
              : normalizedStatus === 'RECEIVED_SUBMITTED'
                ? hasPackageQuantitySource
                  ? receiveQuantityStatus
                  : ManifestPackageStatus.received
                : normalizedStatus === 'RECEIVED'
                  ? hasPackageQuantitySource
                    ? receiveQuantityStatus
                    : ManifestPackageStatus.received
                  : normalizedStatus === 'PARTIALLY_RECEIVED' || normalizedStatus === 'PARTIALLY RECEIVED'
                    ? ManifestPackageStatus.partial
                    : normalizedStatus === 'SUCCESS'
                      ? receiveQuantityStatus
                      : normalizedStatus === 'FAILED' ||
                          normalizedStatus === 'NOT_SELECTED' ||
                          normalizedStatus === 'NOT RECEIVED'
                        ? ManifestPackageStatus.notReceived
                        : pkg.receivedStatus;

        const forceZeroQuantity =
          updatedStatus === ManifestPackageStatus.notReceived ||
          updatedStatus === ManifestPackageStatus.qm ||
          updatedStatus === ManifestPackageStatus.qm_pending;

        return {
          ...pkg,
          quantity: forceZeroQuantity ? 0 : boundedQuantity,
          receivedStatus: updatedStatus
        };
      })
    }))
  };
}
